/**
 * THE WEEK, ONE ROW PER SHIFT.
 *
 * Anthony, 2026-10-07: "a time tracker". The Hours tab lists pay_hours rows
 * with a one-line note, and the punches behind them (In, Out, each meal)
 * were only readable in the payroll export. This lays a week out the way a
 * timesheet reads: Day, In, Out, Meal breaks, Hours, and the California
 * flags as badges, with the week's total and its overtime split.
 *
 * WHAT IS COUNTED. The hours are the pay_hours rows, the same rows the pay
 * run and the Hours tab add up, so this page and payroll can never show
 * two totals. A clocked shift takes its hours from the row clock-out wrote.
 * A shift typed in by hand on the Hours tab is a row too, marked as typed.
 * A shift still on the clock is shown, and counted only once it closes.
 *
 * Overtime is splitOvertime() from overtime.ts, over those same rows.
 * Pure: no network, no DOM.
 */

import { splitOvertime, addSplits, type Split } from "./overtime.ts";
import { shiftMath, dayOf, type BreakSpan, type ClockEdit, type Flag } from "./timeclock.ts";
import { PACIFIC } from "./zoneclock.ts";

export type PunchIn = {
  id: string; technician_id: string; clock_in: string; clock_out: string | null;
  hours_id: string | null; breaks: BreakSpan[];
};
export type HoursIn = {
  id: string; technician_id: string; worked_on: string; hours: number;
  rate: number | null; note: string | null; job_id: string | null;
};

export type SheetRow = {
  key: string;
  technician_id: string;
  /** The Pacific day it counts toward. */
  day: string;
  kind: "clock" | "typed" | "deleted";
  clockId: string | null;
  hoursId: string | null;
  clockIn: string | null;
  clockOut: string | null;
  breaks: BreakSpan[];
  /** What payroll reads for this shift. Zero while it is still open. */
  hours: number;
  open: boolean;
  /** Clocked, closed, and its pay_hours row was removed on the Hours tab. */
  offPayroll: boolean;
  flags: Flag[];
  split: Split | null;
  edits: ClockEdit[];
  /** A typed row's own note, the first line of it. */
  note: string | null;
  sortAt: number;
};

export type PersonWeek = {
  technician_id: string;
  rows: SheetRow[];
  total: number;
  split: Split;
  openNow: boolean;
};

/**
 * Lay out one week. `from` and `to` are the Monday and Sunday the Pay
 * screen pages through. Rows come back oldest first, per person.
 */
export function buildWeek(
  punches: PunchIn[], hours: HoursIn[], edits: ClockEdit[], from: string, to: string, zone = PACIFIC,
): Map<string, PersonWeek> {
  const inWeek = (d: string) => d >= from && d <= to;
  const hoursRows = hours.filter((h) => inWeek(h.worked_on));
  const splits = splitOvertime(hoursRows);
  const byHours = new Map(hoursRows.map((h) => [h.id, h]));
  const editsBy = new Map<string, ClockEdit[]>();
  for (const e of edits) {
    if (!e.clock_id) continue;
    const list = editsBy.get(e.clock_id);
    if (list) list.push(e); else editsBy.set(e.clock_id, [e]);
  }

  const rows: SheetRow[] = [];
  const claimed = new Set<string>();

  for (const p of punches) {
    const day = dayOf(p.clock_in, zone);
    if (!inWeek(day)) continue;
    const h = p.hours_id ? byHours.get(p.hours_id) : undefined;
    if (h) claimed.add(h.id);
    const open = !p.clock_out;
    const math = p.clock_out ? shiftMath(p.clock_in, p.clock_out, p.breaks, zone) : null;
    rows.push({
      key: `c:${p.id}`, technician_id: p.technician_id, day, kind: "clock",
      clockId: p.id, hoursId: h?.id ?? null,
      clockIn: p.clock_in, clockOut: p.clock_out, breaks: p.breaks,
      hours: h ? Number(h.hours) : 0,
      open, offPayroll: !open && !h,
      flags: math?.flags ?? [],
      split: h ? splits.get(h.id) ?? null : null,
      edits: (editsBy.get(p.id) ?? []).slice().sort((a, b) => Date.parse(a.edited_at) - Date.parse(b.edited_at)),
      note: null, sortAt: Date.parse(p.clock_in),
    });
  }

  for (const h of hoursRows) {
    if (claimed.has(h.id)) continue;
    rows.push({
      key: `h:${h.id}`, technician_id: h.technician_id, day: h.worked_on, kind: "typed",
      clockId: null, hoursId: h.id, clockIn: null, clockOut: null, breaks: [],
      hours: Number(h.hours), open: false, offPayroll: false, flags: [],
      split: splits.get(h.id) ?? null, edits: [],
      note: h.note ? (h.note.split("\n")[0] ?? null) : null,
      // A typed row has no time of day. It sorts to the end of its day.
      sortAt: Date.parse(`${h.worked_on}T23:59:59Z`),
    });
  }

  for (const e of edits) {
    if (e.action !== "delete" || !e.before) continue;
    const day = e.before.worked_on ?? dayOf(e.before.clock_in, zone);
    if (!inWeek(day)) continue;
    rows.push({
      key: `d:${e.id}`, technician_id: e.technician_id, day, kind: "deleted",
      clockId: e.clock_id, hoursId: null,
      clockIn: e.before.clock_in, clockOut: e.before.clock_out, breaks: e.before.breaks ?? [],
      hours: 0, open: false, offPayroll: false, flags: [], split: null,
      edits: [...(e.clock_id ? editsBy.get(e.clock_id) ?? [] : [])]
        .sort((a, b) => Date.parse(a.edited_at) - Date.parse(b.edited_at)),
      note: null, sortAt: Date.parse(e.before.clock_in),
    });
  }

  const out = new Map<string, PersonWeek>();
  for (const r of rows.sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.sortAt - b.sortAt))) {
    let w = out.get(r.technician_id);
    if (!w) { w = { technician_id: r.technician_id, rows: [], total: 0, split: { reg: 0, ot: 0, dt: 0 }, openNow: false }; out.set(r.technician_id, w); }
    w.rows.push(r);
    if (r.open) w.openNow = true;
  }
  for (const w of out.values()) {
    const counted = w.rows.filter((r) => r.hoursId && r.kind !== "deleted");
    w.total = Math.round(counted.reduce((t, r) => t + r.hours, 0) * 100) / 100;
    w.split = addSplits(counted.map((r) => r.split ?? { reg: r.hours, ot: 0, dt: 0 }));
  }
  return out;
}

/** "Mon Oct 5" for a Pacific day key. */
export function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC", weekday: "short", month: "short", day: "numeric",
  });
}
